import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Clock3, Copy, Plus, RefreshCw, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import horariosAtendimento from "../../../api/horariosAtendimento";
import FilaZeroSelect from "../../../components/FilaZeroSelect";
import FilaZeroTimeSelect from "../../../components/FilaZeroTimeSelect";
import {
  calcularFimGradeSelect,
  horaParaMinutos,
  normalizarHora,
  obterIntervaloGrade,
  validarSlotGradeDisponibilidade,
} from "../../../utils/horariosDisponibilidadeGrade";
import {
  DIAS_SEMANA,
  DIAS_UTEIS,
  NOMES_DIAS,
  gerarSlotsGrade,
} from "../../../utils/disponibilidadeSemanalUtils";
import "./ConfiguracaoHorariosAtendimento.css";

const OPCOES_DIAS = DIAS_SEMANA.map(([value, label]) => ({ value, label }));

const mensagemErro = (erro, fallback) =>
  erro?.mensagem || erro?.response?.data?.message || erro?.message || fallback;

const TOAST_OPTS = {
  containerId: "fz-configuracoes",
  autoClose: 3500,
};

const toastCfg = {
  success: (msg, extra = {}) =>
    toast.success(msg, { ...TOAST_OPTS, toastId: extra.toastId || `fz-disp-${Date.now()}`, ...extra }),
  error: (msg, extra = {}) =>
    toast.error(msg, { ...TOAST_OPTS, toastId: extra.toastId || `fz-disp-err-${Date.now()}`, ...extra }),
  warning: (msg, extra = {}) =>
    toast.warning(msg, { ...TOAST_OPTS, toastId: extra.toastId || `fz-disp-warn-${Date.now()}`, ...extra }),
  info: (msg, extra = {}) =>
    toast.info(msg, { ...TOAST_OPTS, toastId: extra.toastId || `fz-disp-info-${Date.now()}`, ...extra }),
};

const formatarRotuloIntervalo = (minutos) => {
  if (!minutos) return "livre";
  if (minutos % 60 === 0) {
    const horas = minutos / 60;
    return horas === 1 ? "1 hora" : `${horas} horas`;
  }
  return `${minutos} minutos`;
};

const ConfiguracaoHorariosAtendimento = ({ estabelecimentoId, config = {} }) => {
  const [horarios, setHorarios] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [diaSelecionado, setDiaSelecionado] = useState("segunda");
  const [horaSelecionada, setHoraSelecionada] = useState("09:00");
  const [diasFaixa, setDiasFaixa] = useState(() => new Set(DIAS_UTEIS));
  const [faixaInicio, setFaixaInicio] = useState("09:00");
  const [faixaFim, setFaixaFim] = useState("18:00");

  const carregar = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!estabelecimentoId) return;

      try {
        setLoading(true);
        const resposta = await horariosAtendimento.listar(estabelecimentoId);
        setHorarios(resposta.horarios || {});
      } catch (erro) {
        if (!silencioso) {
          toastCfg.error(
            mensagemErro(erro, "Não foi possível carregar os horários.")
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [estabelecimentoId]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const funcionamento = useCallback(
    (dia) => ({
      ativo: Boolean(config[`horario_${dia}_ativo`]),
      abertura: normalizarHora(config[`horario_${dia}_abertura`]),
      fechamento: normalizarHora(config[`horario_${dia}_fechamento`]),
    }),
    [config]
  );

  const dentroDoFuncionamento = (dia, hora) => {
    const faixa = funcionamento(dia);
    return (
      faixa.ativo &&
      faixa.abertura &&
      faixa.fechamento &&
      horaParaMinutos(hora) >= horaParaMinutos(faixa.abertura) &&
      horaParaMinutos(hora) < horaParaMinutos(faixa.fechamento)
    );
  };

  const idsExistentes = useMemo(
    () =>
      new Set(
        Object.entries(horarios).flatMap(([dia, itens]) =>
          (itens || []).map(
            (item) =>
              `${item.dia_semana || dia}:${normalizarHora(item.hora)}`
          )
        )
      ),
    [horarios]
  );

  const intervaloGrade = useMemo(
    () => obterIntervaloGrade(config),
    [config.intervalo_minimo_atendimento]
  );

  const faixaDiaSelecionado = useMemo(
    () => funcionamento(diaSelecionado),
    [funcionamento, diaSelecionado]
  );

  const faixaReferencia = useMemo(() => {
    const diaUtil = DIAS_SEMANA.find(([dia]) => funcionamento(dia).ativo);
    return diaUtil ? funcionamento(diaUtil[0]) : funcionamento("segunda");
  }, [funcionamento]);

  const validarHorarioGrade = (dia, hora) => {
    const faixa = funcionamento(dia);
    const existentes = (horarios[dia] || []).map((item) =>
      normalizarHora(item.hora)
    );

    return validarSlotGradeDisponibilidade({
      hora,
      intervaloMinutos: intervaloGrade,
      abertura: faixa.abertura,
      fechamento: faixa.fechamento,
      horariosExistentes: existentes,
    });
  };

  const avisarHorarioForaDaGrade = async (validacao) => {
    if (validacao.proximoHorario) {
      const resultado = await Swal.fire({
        title: "Horário fora da grade",
        html: `${validacao.mensagem}<br><br>Deseja usar <strong>${validacao.proximoHorario}</strong>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Usar ${validacao.proximoHorario}`,
        cancelButtonText: "Escolher outro",
        confirmButtonColor: "#ff5a00",
        cancelButtonColor: "#667085",
        reverseButtons: true,
        focusCancel: true,
      });

      return resultado.isConfirmed ? validacao.proximoHorario : null;
    }

    await Swal.fire({
      title: "Horário inválido",
      text: validacao.mensagem,
      icon: "warning",
      confirmButtonColor: "#ff5a00",
    });
    return null;
  };

  const adicionarLote = async (itens, contexto = {}) => {
    const novos = itens.filter(
      (item) => !idsExistentes.has(`${item.dia_semana}:${item.hora}`)
    );

    if (!novos.length) {
      toastCfg.info(
        contexto.acao === "gerarDia"
          ? `Nenhum horário novo para ${NOMES_DIAS[contexto.dia] || "este dia"} — todos já estavam cadastrados.`
          : "Os horários selecionados já estão cadastrados."
      );
      return;
    }

    try {
      setSalvando(true);
      const resposta = await horariosAtendimento.adicionarLote(
        estabelecimentoId,
        novos
      );
      const criados = resposta.total_criados ?? novos.length;
      const erros = resposta.total_erros ?? 0;

      if (criados > 0) {
        const nomeDia = contexto.dia ? NOMES_DIAS[contexto.dia] : null;
        let mensagem;
        if (contexto.acao === "gerarDia" && nomeDia) {
          mensagem =
            criados === 1
              ? `1 horário adicionado em ${nomeDia}.`
              : `${criados} horários adicionados em ${nomeDia}.`;
        } else if (contexto.acao === "gerarSemana") {
          mensagem =
            criados === 1
              ? "1 horário adicionado na semana."
              : `${criados} horários adicionados na semana.`;
        } else {
          mensagem =
            criados === 1
              ? "1 horário adicionado com sucesso."
              : `${criados} horários adicionados com sucesso.`;
        }
        toastCfg.success(mensagem, { toastId: "fz-disp-add-ok" });
      }

      if (erros > 0) {
        const exemplo = resposta.erros?.[0]?.mensagem;
        toastCfg.warning(
          exemplo
            ? `${erros} horário(s) ignorado(s): ${exemplo}`
            : `${erros} horário(s) ignorado(s) por estarem fora da grade.`
        );
      }

      await carregar({ silencioso: true });
    } catch (erro) {
      const detalhe = mensagemErro(erro, "Não foi possível salvar os horários.");
      if (Array.isArray(erro?.erros) && erro.erros.length) {
        toastCfg.error(
          `${detalhe} Ex.: ${erro.erros[0].mensagem || "horário fora da grade."}`
        );
      } else {
        toastCfg.error(detalhe);
      }
    } finally {
      setSalvando(false);
    }
  };

  const gerarSlotsParaDia = (dia, horaInicio = null, horaFim = null) => {
    const faixa = funcionamento(dia);
    if (!faixa.ativo || !faixa.abertura || !faixa.fechamento) return [];

    const intervalo = intervaloGrade > 0 ? intervaloGrade : 30;
    return gerarSlotsGrade({
      abertura: faixa.abertura,
      fechamento: faixa.fechamento,
      intervaloMinutos: intervalo,
      horaInicio: horaInicio || faixa.abertura,
      horaFim: horaFim || faixa.fechamento,
    }).map((hora) => ({
      dia_semana: dia,
      hora,
      ativo: true,
    }));
  };

  const adicionarHorario = async () => {
    if (!dentroDoFuncionamento(diaSelecionado, horaSelecionada)) {
      const faixa = funcionamento(diaSelecionado);
      toastCfg.warning(
        faixa.ativo
          ? `Escolha uma hora entre ${faixa.abertura} e ${faixa.fechamento}.`
          : "Este dia está fechado no horário de funcionamento."
      );
      return;
    }

    let horaFinal = horaSelecionada;
    const validacao = validarHorarioGrade(diaSelecionado, horaFinal);
    if (!validacao.valido) {
      const horaSugerida = await avisarHorarioForaDaGrade(validacao);
      if (!horaSugerida) return;
      horaFinal = horaSugerida;
      setHoraSelecionada(horaSugerida);
    }

    await adicionarLote([
      {
        dia_semana: diaSelecionado,
        hora: horaFinal,
        ativo: true,
      },
    ], { acao: "adicionar", dia: diaSelecionado });
  };

  const gerarSemana = async () => {
    const novos = DIAS_SEMANA.flatMap(([dia]) => gerarSlotsParaDia(dia));

    if (!novos.length) {
      toastCfg.warning(
        "Defina os dias e horários de funcionamento antes de gerar a disponibilidade."
      );
      return;
    }

    await adicionarLote(novos, { acao: "gerarSemana" });
  };

  const gerarDia = async (dia) => {
    const novos = gerarSlotsParaDia(dia);
    if (!novos.length) {
      toastCfg.warning(
        `${NOMES_DIAS[dia]} está fechado ou sem horário de funcionamento.`
      );
      return;
    }
    await adicionarLote(novos, { acao: "gerarDia", dia });
  };

  const aplicarFaixa = async () => {
    if (!diasFaixa.size) {
      toastCfg.warning("Selecione ao menos um dia.");
      return;
    }

    if (
      horaParaMinutos(faixaInicio) == null ||
      horaParaMinutos(faixaFim) == null ||
      horaParaMinutos(faixaInicio) >= horaParaMinutos(faixaFim)
    ) {
      toastCfg.warning("Informe uma faixa válida (início antes do fim).");
      return;
    }

    const novos = [...diasFaixa].flatMap((dia) =>
      gerarSlotsParaDia(dia, faixaInicio, faixaFim)
    );

    if (!novos.length) {
      toastCfg.warning(
        "Nenhum horário da grade cabe na faixa escolhida para os dias selecionados."
      );
      return;
    }

    await adicionarLote(novos);
  };

  const limparDia = async (dia) => {
    const itens = horarios[dia] || [];
    if (!itens.length) {
      toastCfg.info(`Nenhum horário para limpar em ${NOMES_DIAS[dia]}.`);
      return;
    }

    const resultado = await Swal.fire({
      title: "Limpar dia?",
      html: `Remover <strong>${itens.length}</strong> horário(s) de <strong>${NOMES_DIAS[dia]}</strong>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Limpar dia",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#667085",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!resultado.isConfirmed) return;

    try {
      setSalvando(true);
      const { total, erros } = await horariosAtendimento.removerVarios(
        estabelecimentoId,
        itens.map((item) => item.id)
      );

      if (total > 0) {
        toastCfg.success(
          total === 1
            ? `1 horário removido de ${NOMES_DIAS[dia]}.`
            : `${total} horários removidos de ${NOMES_DIAS[dia]}.`,
          { toastId: "fz-disp-limpar-ok" }
        );
      }
      if (erros > 0) {
        toastCfg.warning(`${erros} horário(s) não puderam ser removidos.`);
      }

      await carregar({ silencioso: true });
    } catch (erro) {
      toastCfg.error(mensagemErro(erro, "Não foi possível limpar o dia."));
    } finally {
      setSalvando(false);
    }
  };

  const copiarDia = async (diaOrigem) => {
    const origemItens = horarios[diaOrigem] || [];
    if (!origemItens.length) {
      toastCfg.warning(
        `Cadastre horários em ${NOMES_DIAS[diaOrigem]} antes de copiar.`
      );
      return;
    }

    const opcoes = DIAS_SEMANA.filter(([dia]) => dia !== diaOrigem);
    const html = `
      <p style="margin:0 0 12px;color:#475467;font-size:14px;">
        Copiar ${origemItens.length} horário(s) de <strong>${NOMES_DIAS[diaOrigem]}</strong> para:
      </p>
      <div class="availability-copy-grid">
        ${opcoes
          .map(
            ([value, label]) => `
              <label class="availability-copy-option">
                <input type="checkbox" value="${value}" />
                <span>${label}</span>
              </label>
            `
          )
          .join("")}
      </div>
    `;

    const resultado = await Swal.fire({
      title: "Copiar horários",
      html,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Copiar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ff5a00",
      cancelButtonColor: "#667085",
      reverseButtons: true,
      focusCancel: true,
      preConfirm: () => {
        const selecionados = Array.from(
          Swal.getPopup().querySelectorAll(
            '.availability-copy-option input[type="checkbox"]:checked'
          )
        ).map((input) => input.value);

        if (!selecionados.length) {
          Swal.showValidationMessage("Selecione ao menos um dia.");
          return false;
        }

        return selecionados;
      },
    });

    if (!resultado.isConfirmed || !resultado.value?.length) return;

    const novos = [];
    const alteracoes = [];

    resultado.value.forEach((diaDestino) => {
      origemItens.forEach((itemOrigem) => {
        const hora = normalizarHora(itemOrigem.hora);
        const existente = (horarios[diaDestino] || []).find(
          (item) => normalizarHora(item.hora) === hora
        );

        if (!existente) {
          novos.push({
            dia_semana: diaDestino,
            hora,
            ativo: itemOrigem.ativo,
          });
          return;
        }

        if (existente.ativo !== itemOrigem.ativo) {
          alteracoes.push({ id: existente.id, ativo: itemOrigem.ativo });
        }
      });
    });

    try {
      setSalvando(true);

      if (novos.length) {
        await horariosAtendimento.adicionarLote(estabelecimentoId, novos);
      }

      if (alteracoes.length) {
        await horariosAtendimento.alterarStatusVarios(
          estabelecimentoId,
          alteracoes
        );
      }

      toastCfg.success("Horários copiados com sucesso.");
      await carregar({ silencioso: true });
    } catch (erro) {
      toastCfg.error(mensagemErro(erro, "Não foi possível copiar os horários."));
    } finally {
      setSalvando(false);
    }
  };

  const alternar = async (item) => {
    try {
      await horariosAtendimento.alterarStatus(
        estabelecimentoId,
        item.id,
        !item.ativo
      );
      await carregar({ silencioso: true });
    } catch (erro) {
      toastCfg.error(
        mensagemErro(erro, "Não foi possível atualizar o horário.")
      );
    }
  };

  const remover = async (item, dia) => {
    const resultado = await Swal.fire({
      title: "Remover horário?",
      html: `O horário <strong>${normalizarHora(item.hora)}</strong> de <strong>${NOMES_DIAS[dia]}</strong> deixará de ser oferecido para reserva.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#667085",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!resultado.isConfirmed) return;

    try {
      await horariosAtendimento.remover(estabelecimentoId, item.id);
      toastCfg.success("Horário removido com sucesso.");
      await carregar({ silencioso: true });
    } catch (erro) {
      toastCfg.error(
        mensagemErro(erro, "Não foi possível remover o horário.")
      );
    }
  };

  const toggleDiaFaixa = (dia) => {
    setDiasFaixa((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(dia)) proximo.delete(dia);
      else proximo.add(dia);
      return proximo;
    });
  };

  return (
    <section className="secao-config availability-section">
      <div className="section-heading">
        <div>
          <span className="section-eyebrow">GRADE DE RESERVAS</span>
          <h2>Disponibilidade semanal</h2>
          <p>
            Cadastre os horários oferecidos ao cliente dentro do funcionamento
            definido.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={gerarSemana}
          disabled={salvando}
        >
          <RefreshCw size={17} /> Gerar semana inteira
        </button>
      </div>

      <div className="availability-note">
        <Clock3 size={18} />
        <span>
          {intervaloGrade > 0 ? (
            <>
              Grade de <strong>{formatarRotuloIntervalo(intervaloGrade)}</strong>{" "}
              a partir da abertura de cada dia (ex.: 09:00, 09:30, 10:00).
              Horários fora desse padrão serão bloqueados com sugestão do
              próximo horário válido.
            </>
          ) : (
            <>
              Nenhuma grade fixa configurada. Defina o intervalo na aba
              Agendamento para padronizar os horários oferecidos.
            </>
          )}
        </span>
      </div>

      <div className="availability-note availability-note--secondary">
        <span>
          Azul = disponível · Cinza = inativo (clique no horário para alternar)
          · Laranja = fora do funcionamento.
        </span>
      </div>

      <div className="quick-slot">
        <label className="quick-slot-field">
          <span>Dia</span>
          <FilaZeroSelect
            name="dia_semana"
            value={diaSelecionado}
            options={OPCOES_DIAS}
            onChange={(event) => setDiaSelecionado(event.target.value)}
            isSearchable={false}
          />
        </label>
        <label className="quick-slot-field">
          <span>Hora</span>
          <FilaZeroTimeSelect
            name="hora_disponivel"
            value={horaSelecionada}
            onChange={(event) => setHoraSelecionada(event.target.value)}
            start={faixaDiaSelecionado.abertura || "09:00"}
            end={calcularFimGradeSelect(
              faixaDiaSelecionado.fechamento,
              intervaloGrade || 30
            )}
            step={intervaloGrade > 0 ? intervaloGrade : 30}
          />
        </label>
        <button
          type="button"
          className="add-slot"
          onClick={adicionarHorario}
          disabled={salvando}
        >
          <Plus size={17} /> Adicionar horário
        </button>
      </div>

      <details className="range-panel-details">
        <summary>Adicionar faixa de horários (opcional)</summary>
        <div className="range-panel">
          <p className="range-panel-help">
            Cria vários horários de uma vez nos dias selecionados.
          </p>

          <div className="range-panel-days">
            {DIAS_SEMANA.map(([dia, label]) => (
              <label key={dia} className="range-day-chip">
                <input
                  type="checkbox"
                  checked={diasFaixa.has(dia)}
                  onChange={() => toggleDiaFaixa(dia)}
                />
                <span>{label.slice(0, 3)}</span>
              </label>
            ))}
          </div>

          <div className="range-panel-shortcuts">
            <button
              type="button"
              className="range-shortcut"
              onClick={() => setDiasFaixa(new Set(DIAS_UTEIS))}
            >
              Dias úteis
            </button>
            <button
              type="button"
              className="range-shortcut"
              onClick={() =>
                setDiasFaixa(new Set(DIAS_SEMANA.map(([dia]) => dia)))
              }
            >
              Todos
            </button>
          </div>

          <div className="range-panel-controls">
            <label className="range-panel-field">
              <span>De</span>
              <FilaZeroTimeSelect
                name="faixa_inicio"
                value={faixaInicio}
                onChange={(event) => setFaixaInicio(event.target.value)}
                start={faixaReferencia.abertura || "09:00"}
                end={calcularFimGradeSelect(
                  faixaReferencia.fechamento,
                  intervaloGrade || 30
                )}
                step={intervaloGrade > 0 ? intervaloGrade : 30}
              />
            </label>
            <label className="range-panel-field">
              <span>Até</span>
              <FilaZeroTimeSelect
                name="faixa_fim"
                value={faixaFim}
                onChange={(event) => setFaixaFim(event.target.value)}
                start={faixaReferencia.abertura || "09:00"}
                end={faixaReferencia.fechamento || "22:00"}
                step={intervaloGrade > 0 ? intervaloGrade : 30}
              />
            </label>
            <button
              type="button"
              className="range-apply"
              onClick={aplicarFaixa}
              disabled={salvando}
            >
              Aplicar faixa
            </button>
          </div>
        </div>
      </details>

      {loading ? (
        <div className="availability-loading">Carregando disponibilidade...</div>
      ) : (
        <div className="availability-grid">
          {DIAS_SEMANA.map(([dia, label]) => {
            const faixa = funcionamento(dia);
            const itens = [...(horarios[dia] || [])].sort((a, b) =>
              String(a.hora).localeCompare(String(b.hora))
            );

            return (
              <article className="availability-day" key={dia}>
                <header className="availability-day-header">
                  <div>
                    <strong>{label}</strong>
                    <span>
                      {faixa.ativo
                        ? `${faixa.abertura}–${faixa.fechamento}`
                        : "Fechado"}
                    </span>
                  </div>
                  <b>{itens.filter((item) => item.ativo).length}</b>
                </header>

                <div className="day-actions">
                  <button
                    type="button"
                    className="day-action"
                    onClick={() => gerarDia(dia)}
                    disabled={salvando || !faixa.ativo}
                  >
                    <RefreshCw size={14} /> Gerar dia
                  </button>
                  <button
                    type="button"
                    className="day-action"
                    onClick={() => copiarDia(dia)}
                    disabled={salvando || !itens.length}
                  >
                    <Copy size={14} /> Copiar
                  </button>
                  <button
                    type="button"
                    className="day-action day-action--danger"
                    onClick={() => limparDia(dia)}
                    disabled={salvando || !itens.length}
                  >
                    <Trash2 size={14} /> Limpar
                  </button>
                </div>

                <div className="slot-list">
                  {itens.length ? (
                    itens.map((item) => {
                      const fora = !dentroDoFuncionamento(dia, item.hora);
                      const estado = fora
                        ? "is-outside"
                        : item.ativo
                          ? "is-available"
                          : "is-inactive";

                      return (
                        <div className={`slot ${estado}`} key={item.id}>
                          <button
                            type="button"
                            className="slot-time"
                            onClick={() => alternar(item)}
                            title="Ativar ou inativar"
                          >
                            {normalizarHora(item.hora)}
                          </button>
                          <button
                            type="button"
                            className="slot-remove"
                            onClick={() => remover(item, dia)}
                            title="Remover horário"
                            aria-label={`Remover ${normalizarHora(item.hora)} de ${label}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <span className="empty-slots">Nenhum horário</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ConfiguracaoHorariosAtendimento;
